import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { Link } from 'react-router-dom';

const FAQPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [faqRef, faqInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Preguntas Frecuentes sobre Pladur en Badalona | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Resuelve tus dudas sobre instalación de pladur en Badalona: plazos, precios, aislamiento, acabados y más. Respuestas de profesionales.');
    }

    // Add FAQPage Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const faqs = [
    {
      question: '¿Cuánto tarda una instalación de pladur?',
      answer: 'Los plazos varían según el tipo de proyecto. Un tabique estándar de una habitación se puede instalar en 1-2 días. Un falso techo de una estancia suele tardar 1 día. Un trasdosado completo de una vivienda puede llevar 3-5 días. En el presupuesto te indicaremos los plazos exactos para tu proyecto específico.',
      link: '/contacto',
      linkText: 'Solicitar presupuesto',
    },
    {
      question: '¿Qué factores afectan al precio de una instalación de pladur?',
      answer: 'El precio depende de varios factores: metros cuadrados a instalar, altura de los techos, tipo de aislamiento (acústico o térmico), complejidad de accesos, nivel de acabado requerido, número de puertas o registros, y si se trata de tabiques, trasdosados o techos. Cada proyecto es único y elaboramos presupuestos personalizados.',
      link: '/servicios',
      linkText: 'Ver todos los servicios',
    },
    {
      question: '¿El pladur aísla del ruido?',
      answer: 'Sí, el pladur con aislamiento acústico es muy efectivo para reducir el ruido. Utilizamos lana de roca de alta densidad en el interior de tabiques y trasdosados, y en casos que requieren mayor aislamiento, instalamos sistemas de doble placa con bandas resilientes. Es ideal para mejorar la privacidad entre habitaciones o reducir el ruido de vecinos.',
      link: '/servicios#aislamiento-acustico',
      linkText: 'Más sobre aislamiento acústico',
    },
    {
      question: '¿El pladur mejora el aislamiento térmico?',
      answer: 'Sí, los trasdosados con aislamiento térmico (lana mineral o poliestireno) mejoran significativamente la eficiencia energética. Reducen la pérdida de calor en invierno y mantienen el frescor en verano, lo que se traduce en ahorro en calefacción y aire acondicionado. También ayuda a mejorar el certificado energético de la vivienda.',
      link: '/servicios#aislamiento-termico',
      linkText: 'Más sobre aislamiento térmico',
    },
    {
      question: '¿Se puede instalar pladur en baños y cocinas?',
      answer: 'Sí, para zonas húmedas utilizamos placas de pladur antihumedad (color verde), que están especialmente diseñadas para resistir ambientes con alta humedad. Son perfectas para baños, cocinas, lavaderos y sótanos. Estas placas previenen la aparición de moho y hongos, y son totalmente compatibles con alicatado posterior.',
      link: '/servicios#antihumedad',
      linkText: 'Más sobre pladur antihumedad',
    },
    {
      question: '¿Los acabados quedan listos para pintar?',
      answer: 'Sí, entregamos la obra con acabados perfectos, listos para pintar directamente. Realizamos el tratamiento de juntas con cinta y pasta, lijado fino y limpieza completa. La superficie queda completamente lisa y uniforme, preparada para aplicar la pintura que prefieras.',
      link: '/empresa',
      linkText: 'Conoce nuestra metodología',
    },
    {
      question: '¿Trabajáis con la vivienda ocupada?',
      answer: 'Sí, trabajamos habitualmente en viviendas y oficinas ocupadas. Coordinamos horarios flexibles para minimizar molestias, protegemos muebles y suelos, y mantenemos el espacio limpio y ordenado durante toda la obra. Al final de cada jornada recogemos y limpiamos la zona de trabajo.',
      link: '/empresa',
      linkText: 'Cómo trabajamos',
    },
    {
      question: '¿Qué horarios de trabajo tenéis?',
      answer: 'Nuestro horario habitual es de lunes a viernes de 8:00 a 19:00. Podemos adaptar los horarios según tus necesidades y las normativas de la comunidad de vecinos. Consultamos contigo los mejores horarios antes de comenzar la obra.',
      link: '/contacto',
      linkText: 'Contactar',
    },
    {
      question: '¿Se necesitan permisos para instalar pladur?',
      answer: 'En la mayoría de casos no se necesitan permisos especiales, ya que el pladur se considera obra menor. Sin embargo, si la reforma implica cambios estructurales importantes o afecta a elementos comunes del edificio, puede ser necesario comunicarlo a la comunidad de vecinos o solicitar licencia municipal. Te asesoramos en cada caso.',
      link: '/contacto',
      linkText: 'Consultar sin compromiso',
    },
    {
      question: '¿Emitís factura por los trabajos?',
      answer: 'Sí, emitimos factura por todos nuestros trabajos. Además, ofrecemos garantía sobre la instalación realizada. Trabajamos de forma totalmente legal y transparente, cumpliendo con todas las obligaciones fiscales.',
      link: '/empresa',
      linkText: 'Sobre PLADUREX',
    },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-36 md:pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
            >
              <span className="text-orange-primary text-sm font-bold">Preguntas frecuentes</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-text-primary mb-6"
            >
              Resolvemos tus dudas sobre pladur
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed"
            >
              Encuentra respuestas a las preguntas más comunes sobre <Link to="/servicios" className="text-orange-primary hover:underline">instalación de pladur</Link> en Badalona
            </motion.p>
          </div>
        </section>

        {/* FAQ Section */}
        <section ref={faqRef} className="py-12 px-6 bg-dark-bg">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={faqInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.05 * index }}
                  className="bg-dark-card border border-dark-border rounded-xl overflow-hidden hover:border-orange-primary/50 transition-colors duration-300"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-dark-bg/50 transition-colors duration-300"
                  >
                    <span className="text-text-primary font-bold text-lg pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-orange-primary text-2xl transition-transform duration-300 flex-shrink-0 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{
                      height: openIndex === index ? 'auto' : 0,
                      opacity: openIndex === index ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6">
                      <p className="text-text-secondary leading-relaxed text-[15px] mb-4">
                        {faq.answer}
                      </p>
                      {faq.link && (
                        <Link
                          to={faq.link}
                          className="inline-flex items-center space-x-1 text-orange-primary hover:underline text-sm font-medium"
                        >
                          <span>{faq.linkText}</span>
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 bg-dark-card">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-extrabold text-text-primary mb-6">
                ¿Tienes más preguntas?
              </h2>
              <p className="text-text-secondary text-lg mb-10">
                <Link to="/contacto" className="text-orange-primary hover:underline">Contacta con nosotros</Link> y te asesoramos personalmente sobre tu proyecto
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contacto"
                  className="px-8 py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 font-bold text-lg whitespace-nowrap inline-flex items-center justify-center space-x-2"
                >
                  <i className="ri-mail-line"></i>
                  <span>Solicitar presupuesto</span>
                </Link>
                <a
                  href="https://wa.me/34629501941"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border-2 border-orange-primary text-orange-primary rounded-full hover:bg-orange-primary hover:text-white transition-all duration-300 font-bold text-lg whitespace-nowrap inline-flex items-center justify-center space-x-2"
                >
                  <i className="ri-whatsapp-line"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
              <p className="text-text-muted text-sm mt-8">
                También puedes <Link to="/empresa" className="text-orange-primary hover:underline">conocer más sobre nuestra empresa</Link> y metodología de trabajo
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default FAQPage;
